import { useState, useMemo } from "react";
import { formatDate } from "../../domain/eventoDetalleUtils";
import { IoWarningOutline } from "react-icons/io5";
import { isPdf, isExcel, isImage } from "../../domain/eventoDetalleUtils";
import styles from "../../styles/evento-detalle.module.css";
import {
    generateSolicitudLicenciaDocx,
    buildSolicitudLicenciaDataFromCaceria,
} from "../../domain/generateLicenciaWord";

export function TabCacerias({ evento }) {
    const [openSet, setOpenSet] = useState(new Set());

    const toggleOpen = (id) =>
        setOpenSet((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });

    const isOpen = (id) => openSet.has(id);

    const list = useMemo(() => {
        const raw = Array.isArray(evento?.cacerias) ? evento.cacerias : [];
        return [...raw].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    }, [evento]);

    const armaLabel = (arma) => {
        if (!arma) return "—";
        const tipoRaw = String(arma.tipoArmaDescripcion ?? arma.tipo ?? "").trim();
        const tipo3 = tipoRaw ? tipoRaw.slice(0, 3) : "—";
        const calibre = String(arma.calibre ?? "—").trim() || "—";
        const serie = String(arma.numeroSerie ?? "—").trim() || "—";
        return `${tipo3} • ${calibre} • ${serie}`;
    };

    const getCaceriaAssociations = (caceria) => {
        const parts = Array.isArray(caceria?.participaciones) ? caceria.participaciones : [];

        const isCazador = (p) => {
            const r = p?.rol;
            if (!r) return false;
            if (typeof r === "string") return r === "CAZADOR";
            if (typeof r === "object") return r.rol === "CAZADOR";
            return false;
        };

        const hunters = parts
            .filter(isCazador)
            .map((pc) => ({
                hunter: pc.persona || null,
                arma1: pc.arma1 || null,
                arma2: pc.arma2 || null,
                secretario: pc.secretario || null,
                cargador: pc.cargador || null,
            }));

        return { hunters };
    };

    if (!list.length) {
        return <div className={styles.hint}>Este evento no tiene cacerías.</div>;
    }

    // OJO: esta función recibe UNA CACERÍA (c), no el evento del click
    const onGenerarLicenciaCaceria = async (c) => {
        try {
            if (!c?.id) {
                alert("Cacería inválida (no llega el objeto). Revisa el onClick.");
                return;
            }

            // Filtrar solo cazadores en vigor
            const parts = Array.isArray(c?.participaciones) ? c.participaciones : [];
            const isCazador = (p) => {
                const r = p?.rol;
                if (!r) return false;
                if (typeof r === "string") return r === "CAZADOR";
                if (typeof r === "object") return r.rol === "CAZADOR";
                return false;
            };

            const partsCazadoresEnVigor = parts.filter((pc) => {
                if (!isCazador(pc)) return false;

                const hunter = pc?.persona;
                // En vigor => NO tieneAdvertencia
                return hunter && !hunter.tieneAdvertencia;
            });

            const cFiltrada = {
                ...c,
                participaciones: partsCazadoresEnVigor,
            };

            if (!partsCazadoresEnVigor.length) {
                alert("No hay cazadores con documentación en vigor para esta cacería.");
                return;
            }

            const data = buildSolicitudLicenciaDataFromCaceria({
                caceria: cFiltrada,
                fechaFirma: new Date(),
            });

            await generateSolicitudLicenciaDocx({
                templateUrl: "/templates/solicitud-licencia-template.docx",
                data,
                fileName: `Solicitud_licencia_caceria_${c.id}_evento_${evento?.id ?? ""}.docx`,
            });
        } catch (e) {
            console.error(e);
            alert("No se pudo generar el documento. Revisa la plantilla y los tags.");
        }
    };

    return (
        <div className={styles.stack}>
            {list.map((c) => {
                const open = isOpen(c.id);
                const { hunters } = getCaceriaAssociations(c);

                const resumen = [
                    c.tipoCaceriaDescripcion || c.tipoCaceria || "—",
                    hunters.length ? `${hunters.length} cazador(es)` : "Sin cazadores",
                ]
                    .filter(Boolean)
                    .join(" • ");

                return (
                    <article
                        key={c.id}
                        className={`${styles["item-card"]} ${styles.accordion} ${open ? styles.open : ""}`}
                    >
                        <button
                            type="button"
                            className={styles["accordion-header"]}
                            onClick={() => toggleOpen(c.id)}
                            aria-expanded={open}
                            aria-controls={`caceria-body-${c.id}`}
                        >
                            <div className={styles["accordion-title"]}>
                                <strong>{formatDate(c.fecha)}</strong>
                                <span className={styles.muted}>— {resumen}</span>

                                {c.tieneAdvertencia && (
                                    <span
                                        className={`${styles.pill} ${styles.danger}`}
                                        title="Algún participante tiene documentación caducada"
                                        style={{ marginLeft: 8 }}
                                    >
                                        <IoWarningOutline /> Alerta
                                    </span>
                                )}
                            </div>

                            <span className={styles.chevron} aria-hidden>
                                ▾
                            </span>
                        </button>

                        <div id={`caceria-body-${c.id}`} className={styles["accordion-body"]}>
                            <div className={styles["item-row"]}>
                                {hunters.length === 0 ? (
                                    <div className={styles["empty-inline"]}>Sin participantes.</div>
                                ) : (
                                    <div className={styles["responsive-table"]}>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Cazador</th>
                                                    <th>Arma1</th>
                                                    <th>Arma2</th>
                                                    <th>Secretario</th>
                                                    <th>Cargador</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {hunters.map(({ hunter, arma1, arma2, secretario, cargador }, i) => (
                                                    <tr key={`h-${hunter?.id ?? i}`}>
                                                        <td data-label="Participante">
                                                            <strong>
                                                                {hunter ? `${hunter.nombre} ${hunter.apellidos}` : "—"}
                                                            </strong>
                                                            {hunter?.tieneAdvertencia && (
                                                                <IoWarningOutline
                                                                    className={styles["warn-icon-inline"]}
                                                                    title="Documentación caducada"
                                                                    style={{ marginLeft: 6 }}
                                                                />
                                                            )}
                                                        </td>
                                                        <td data-label="Arma 1">{armaLabel(arma1)}</td>
                                                        <td data-label="Arma 2">{armaLabel(arma2)}</td>
                                                        <td data-label="Secretario">
                                                            {secretario ? `${secretario.nombre} ${secretario.apellidos}` : "—"}
                                                        </td>
                                                        <td data-label="Cargador">
                                                            {cargador ? `${cargador.nombre} ${cargador.apellidos}` : "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className={styles["item-actions"]} style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                    {c.rutaDocumento ? (
                                        <a
                                            href={c.rutaDocumento}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={`${styles.btn} ${styles.ghost}`}
                                            title={
                                                isPdf(c.rutaDocumento)
                                                    ? "Ver PDF"
                                                    : isExcel(c.rutaDocumento)
                                                        ? "Abrir Excel/CSV"
                                                        : isImage(c.rutaDocumento)
                                                            ? "Ver imagen"
                                                            : "Abrir documento"
                                            }
                                        >
                                            Ver documento
                                        </a>
                                    ) : (
                                        <span className={styles.muted}>Sin documento</span>
                                    )}

                                    <div>
                                        <button
                                            type="button"
                                            className={`${styles.btn} ${styles.btnPrimary}`}
                                            onClick={() => onGenerarLicenciaCaceria(c)}
                                        >
                                            Generar Documentación Medioambiente
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </article>
                );
            })}
        </div>
    );
}
